<template>
  <div v-if="visible" class="sheet-scrim feedback-scrim" @click.self="close">
    <div class="sheet feedback-card" role="dialog" aria-modal="true" aria-labelledby="feedbackTitle">
      <div class="sheet-head">
        <h2 id="feedbackTitle" class="sheet-title">{{ sent ? 'Sent' : 'Send feedback' }}</h2>
        <button type="button" class="icon-btn icon-btn-filled icon-btn-sm" @click="close" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="sent" class="sheet-body feedback-body feedback-done" role="status">
        <i class="fas fa-check-circle"></i>
        <p>Thanks, we read every one.</p>
      </div>

      <form v-else class="sheet-body feedback-body" @submit.prevent="submit">
        <div class="segmented" role="radiogroup" aria-label="Type">
          <button
            v-for="t in types"
            :key="t.value"
            type="button"
            role="radio"
            :aria-checked="type === t.value ? 'true' : 'false'"
            :class="{ 'is-active': type === t.value }"
            @click="type = t.value"
          >{{ t.label }}</button>
        </div>

        <div>
          <label class="field-label" for="feedbackMessage">{{ type === 'bug' ? 'What went wrong?' : 'What\'s on your mind?' }}</label>
          <textarea
            id="feedbackMessage"
            v-model="message"
            class="field feedback-message"
            rows="5"
            maxlength="2000"
            required
            :placeholder="type === 'bug' ? 'What you did, what you expected, what happened' : ''"
          ></textarea>
        </div>

        <div>
          <label class="field-label" for="feedbackEmail">Email <span class="feedback-optional">(only if you want a reply)</span></label>
          <input id="feedbackEmail" v-model="email" class="field" type="email" autocomplete="email" maxlength="254">
        </div>

        <details class="feedback-context">
          <summary>Included with your report</summary>
          <p>This page's details, so we can reproduce it. No audio.</p>
          <pre>{{ contextText }}</pre>
        </details>

        <p v-if="error" class="feedback-error" role="alert">{{ error }}</p>

        <button type="submit" class="btn btn-primary btn-block" :disabled="sending || message.trim().length < 5">
          <i class="fas" :class="sending ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'"></i>
          {{ sending ? 'Sending' : 'Send' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { sendFeedback } from '../../services/feedback';

const CLOSE_AFTER_SENT_MS = 1800;

export default {
  name: 'FeedbackSheet',
  data() {
    return {
      types: [
        { value: 'bug', label: 'Bug' },
        { value: 'idea', label: 'Idea' },
        { value: 'other', label: 'Other' }
      ],
      type: 'idea',
      message: '',
      email: '',
      sending: false,
      sent: false,
      error: ''
    };
  },
  computed: {
    feedback() {
      return this.$store.state.settings.feedback;
    },
    visible() {
      return !!this.feedback;
    },
    contextText() {
      return JSON.stringify(this.feedback ? this.feedback.context : {}, null, 2);
    }
  },
  watch: {
    visible(isVisible) {
      if (!isVisible) return;
      const user = this.$store.state.auth.user;
      this.type = this.feedback.type;
      this.email = (user && user.email) || '';
      this.message = '';
      this.sending = false;
      this.sent = false;
      this.error = '';
      this.track('feedback_opened');
    }
  },
  beforeDestroy() {
    clearTimeout(this._closeTimer);
  },
  methods: {
    close() {
      clearTimeout(this._closeTimer);
      this.$store.dispatch('settings/closeFeedback');
    },

    submit() {
      if (this.sending) return;
      const self = this;
      this.sending = true;
      this.error = '';
      sendFeedback({
        type: this.type,
        message: this.message.trim(),
        email: this.email.trim(),
        context: this.feedback.context
      }).then(function() {
        self.sending = false;
        self.sent = true;
        self.track('feedback_sent');
        self._closeTimer = setTimeout(function() { self.close(); }, CLOSE_AFTER_SENT_MS);
      }, function(err) {
        // The typed message stays put; only the error line changes.
        self.sending = false;
        self.error = self.errorMessage(err && err.code);
      });
    },

    errorMessage(code) {
      if (code === 'RATE_LIMITED') return 'Too many reports just now. Try again in a minute.';
      if (code === 'INVALID') return 'Check the message (5 to 2000 characters) and the email address.';
      if (code === 'NETWORK') return 'Could not reach the server. Check your connection and try again.';
      return 'Something went wrong sending that. Please try again.';
    },

    track(action) {
      if (window.$analytics) {
        window.$analytics.trackEvent(action, { category: 'Feedback', label: this.type });
      }
    }
  }
}
</script>

<style scoped>
/* Same layer as the share sheet: above the action bar, below the tutorial. */
.feedback-scrim { z-index: 1500; }

.feedback-card { max-width: 440px; }

.feedback-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* .field is sized for one line. */
.feedback-message {
  height: auto;
  min-height: 120px;
  padding: 10px 14px;
  line-height: 1.45;
  resize: vertical;
  font-family: inherit;
}

.feedback-optional {
  font-weight: 400;
  color: var(--faint);
}

.feedback-context {
  font-size: 13px;
  color: var(--muted);
}

.feedback-context summary {
  cursor: pointer;
}

.feedback-context p {
  margin: 8px 0 6px;
}

.feedback-context pre {
  margin: 0;
  max-height: 160px;
  overflow: auto;
  padding: 10px 12px;
  border-radius: var(--r-control);
  background: var(--surface-2);
  font-size: 11px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.feedback-error {
  margin: 0;
  font-size: 13px;
  color: var(--fail);
}

.feedback-done {
  align-items: center;
  padding-top: 16px;
  padding-bottom: 32px;
  text-align: center;
}

.feedback-done i {
  font-size: 36px;
  color: var(--pass);
}

.feedback-done p {
  margin: 0;
  font-size: 16px;
}
</style>
